import dayjs from "dayjs";

// Function to process data and create grid columns and rows
export const processReportData = (extensionRequestList, appliedFilters = null, filters, extensionTypeOptions) => {
  if (!extensionRequestList || extensionRequestList.length === 0) {
    return { processedColumns: [], processedRows: [] };
  }

  // Generate months based on applied filters or default to last 6 months
  let monthsToShow = [];
  
  // Check if we have applied filter parameters for date range
  if (appliedFilters && appliedFilters.startMonth && appliedFilters.endMonth && appliedFilters.startYear && appliedFilters.endYear) {
    // Generate months from start to end based on applied filters
    const startDate = dayjs(`${appliedFilters.startYear}-${String(appliedFilters.startMonth).padStart(2, '0')}-01`);
    const endDate = dayjs(`${appliedFilters.endYear}-${String(appliedFilters.endMonth).padStart(2, '0')}-01`);
    
    let currentDate = startDate;
    // Use comparison with unix timestamps to avoid plugin dependency
    while (currentDate.valueOf() <= endDate.valueOf()) {
      monthsToShow.push({
        key: currentDate.format('YYYY-MM'),
        label: currentDate.format('MMM-YY')
      });
      currentDate = currentDate.add(1, 'month');
    }
  } else {
    // Default: show last 6 months if no filters are applied
    const currentDate = dayjs();
    for (let i = 5; i >= 0; i--) {
      const monthDate = currentDate.subtract(i, 'month');
      monthsToShow.push({
        key: monthDate.format('YYYY-MM'),
        label: monthDate.format('MMM-YY')
      });
    }
  }

  // Process data by extension type and month
  const dataMap = {};

  // Count items and sum values by extension type and month based on processing time
  extensionRequestList.forEach((item, index) => {
    if (item.extensionType && item.processingtime) {
      const processingMonth = dayjs(item.processingtime).format('YYYY-MM');
      let extTypeKey = item.extensionType;
      
      // Handle EF type differentiation based on newReasonCode
      if (item.extensionType === 'EF') {
        if (item.newReasonCode && item.newReasonCode.startsWith('CF')) {
          extTypeKey = 'EF+CF';
        } else if (item.newReasonCode && item.newReasonCode.startsWith('CL')) {
          extTypeKey = 'EF+CL';
        }
      }
              
      // Ensure the extension type exists in dataMap
      if (!dataMap[extTypeKey]) {
        dataMap[extTypeKey] = {};
        monthsToShow.forEach(month => {
          dataMap[extTypeKey][month.key] = { count: 0, value: 0 };
        });
      }
      
      if (dataMap[extTypeKey][processingMonth]) {
        dataMap[extTypeKey][processingMonth].count++;
        // Add the amount to extend for value calculation
        const amountToExtend = parseFloat(item.amountToExtend) || 0;
        dataMap[extTypeKey][processingMonth].value += amountToExtend;
      }
    }
  });

  // Determine column header suffix based on current volume type (immediate effect)
  const volumeType = filters.volume;

  // Create columns - show all months from the actual data
  const processedColumns = [
    {
      field: "extensionType",
      headerName: "Extension Type",
      width: 150,
      align: "left",
      headerAlign: "left",
      filterable: true,
      type: 'singleSelect',
      valueOptions: extensionTypeOptions,
    },
    ...monthsToShow.map(month => ({
      field: month.key,
      headerName: month.label,
      width: 140,
      align: "center",
      headerAlign: "center",
      type: 'number',
      filterable: true
    }))
  ];

  // Create rows - show only extension types that exist in the actual data
  const allExtensionTypes = [...new Set(Object.keys(dataMap))].filter(type => type !== 'EF');
  
  // Custom sorting function for extension types: IE → EF+CF → EF+CL → TI → LO → LD → TD → RR → AP
  const sortExtensionTypes = (types) => {
    const priorityOrder = ['IE', 'EF+CF', 'EF+CL', 'TI', 'LO', 'LD', 'TD', 'RR', 'AP'];
    
    return types.sort((a, b) => {
      const indexA = priorityOrder.indexOf(a);
      const indexB = priorityOrder.indexOf(b);
      
      // If both are in priority list, sort by priority order
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
      // If only a is in priority list, a comes first
      if (indexA !== -1 && indexB === -1) {
        return -1;
      }
      // If only b is in priority list, b comes first
      if (indexA === -1 && indexB !== -1) {
        return 1;
      }
      // If neither is in priority list, sort alphabetically
      return a.localeCompare(b);
    });
  };
  
  const sortedExtensionTypes = sortExtensionTypes(allExtensionTypes);
  
  const processedRows = sortedExtensionTypes.map((extType, index) => {
    const row = {
      id: index,
      extensionType: extType
    };

    monthsToShow.forEach(month => {    
      // Check if this extension type exists in dataMap and has data for this month
      let monthData = { count: 0, value: 0 };
      if (dataMap[extType] && dataMap[extType][month.key]) {
        monthData = dataMap[extType][month.key];
      }
      
      const value = volumeType === 'value' ? monthData.value : monthData.count;
      // Replace 0 with "-" when there's no data
      row[month.key] = value === 0 ? "-" : value;
    });

    return row;
  });
  
  return { processedColumns, processedRows };
};

// Get grid data with applied filters (only after Apply button is clicked)
export const getFilteredData = (reportData, appliedFilters) => {
  if (!reportData || reportData.length === 0) return [];
  
  let filteredData = [...reportData];
  
  // Apply extension type filter only from applied filters (not current selections)
  if (appliedFilters && appliedFilters.extensionTypes.length > 0) {
    filteredData = filteredData.filter(item => 
      appliedFilters.extensionTypes.includes(item.extensionType)
    );
  }
  
  return filteredData;
};

// Prepare data for chart visualization
export const getChartData = (filteredData, columns) => {
  if (!filteredData || filteredData.length === 0) return [];

  // Get month keys from columns (excluding extensionType column)
  const monthKeys = columns
    .filter(col => col.field !== 'extensionType')
    .map(col => ({ key: col.field, label: col.headerName }));

  // Transform data for chart
  const chartData = monthKeys.map(month => {
    const dataPoint = { month: month.label };
    
    filteredData.forEach(row => {
      // Use numeric value for chart (convert "-" back to 0 for chart display)
      const value = row[month.key];
      if (value === "-") {
        dataPoint[row.extensionType] = 0;
      } else {
        dataPoint[row.extensionType] = typeof value === 'string' ? parseFloat(value) : (value || 0);
      }
    });
    
    return dataPoint;
  });

  return chartData;
};

// Get unique extension types for chart colors
export const getExtensionTypesForChart = (filteredData) => {
  return filteredData.map(row => row.extensionType);
};
